import React, { useState } from "react";
import { MessageSquarePlus, MessageSquareText, X, RotateCcw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePresentationMode } from "./PresentationContext";
import { useRoadmapVisibilityContext } from "./RoadmapVisibilityContext";
import { useNotes } from "./NotesContext";
import { hexToRgba } from "./colorUtils";
import ProductPill from "./ProductPill";
import NoteDialog from "./NoteDialog";

export const NoteableItem = ({ item, section = "", accent = "#3F4F66", index = 0, numbered = false, showPills = true, product = null, allowExclude = false }) => {
  const { noteCount } = useNotes();
  const presenting = usePresentationMode();
  const visibility = useRoadmapVisibilityContext();
  const [open, setOpen] = useState(false);
  if (!item || (presenting && item.excluded_from_presentation)) return null;
  const count = noteCount(item.key);
  const body = item.title || item.text || "";
  const excluded = Boolean(item.excluded_from_presentation);
  const canExclude = allowExclude && !presenting && visibility && item.key;
  const prefix = `${section}-${item.key}`;
  return (
    <>
      <div className={`flex min-w-0 items-start gap-1 ${excluded ? "roadmap-item-excluded" : ""}`} data-testid={`roadmap-item-${prefix}`} data-excluded={excluded}>
        <div role={presenting ? undefined : "button"} tabIndex={presenting ? undefined : 0}
          className={`noteable flex min-w-0 flex-1 gap-3 items-start p-2 ${presenting ? "noteable--readonly" : ""}`}
          onClick={presenting ? undefined : () => setOpen(true)}
          onKeyDown={presenting ? undefined : (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setOpen(true); } }}
          data-testid={`noteable-${prefix}`} data-item-key={item.key}
          title={presenting ? undefined : excluded ? "Omitted from Presentation Mode. Click to leave a note." : "Click to leave a note"}>
          {numbered ? (
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold shrink-0 mt-0.5" style={{ background: hexToRgba(accent, 0.12), color: "hsl(var(--foreground))" }} aria-hidden="true">{index + 1}</span>
          ) : <span className="inline-block w-2 h-2 rounded-full shrink-0 mt-2" style={{ background: accent }} aria-hidden="true" />}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className={`text-sm leading-relaxed text-foreground break-words [overflow-wrap:anywhere] ${item.title ? "font-semibold" : ""}`} data-testid={`roadmap-item-text-${prefix}`}>{body}</span>
              {item.is_new && <span className="new-marker inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide" data-testid={`new-since-${prefix}`}>New</span>}
            </div>
            {item.detail && <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed break-words [overflow-wrap:anywhere]" data-testid={`roadmap-item-detail-${prefix}`}>{item.detail}</p>}
            {excluded && <span className="text-xs text-muted-foreground" data-testid={`roadmap-item-excluded-label-${prefix}`}>Hidden in Presentation Mode</span>}
            {showPills && item.products?.length > 0 && <div className="flex flex-wrap gap-1.5 mt-1.5">{item.products.map((p) => <ProductPill key={p.slug} product={p} testId={`roadmap-product-${prefix}-${p.slug}`} />)}</div>}
          </div>
          {!presenting && <span className="shrink-0 mt-0.5 inline-flex items-center gap-1 text-muted-foreground" aria-hidden="true">
            {count > 0 ? <><MessageSquareText size={15} /><span className="text-xs font-semibold">{count}</span></> : <MessageSquarePlus size={15} className="opacity-45" />}
          </span>}
        </div>
        {canExclude && <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0 text-muted-foreground"
          disabled={visibility.pending[item.key]}
          onClick={() => excluded ? visibility.restore(item.hidden_record_id, item.key) : visibility.exclude(item, product)}
          aria-label={`${excluded ? "Restore to" : "Hide from"} Presentation Mode: ${item.glance_title || body}`}
          title={excluded ? "Restore to Presentation Mode" : "Grey out here and hide in Presentation Mode"}
          data-testid={`${excluded ? "detail-row-restore" : "detail-row-exclude"}-${prefix}`}>
          {visibility.pending[item.key] ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : excluded ? <RotateCcw size={16} aria-hidden="true" /> : <X size={16} aria-hidden="true" />}
        </Button>}
      </div>
      {canExclude && visibility.errors[item.key] && <p role="alert" className="text-xs text-destructive px-2" data-testid={`detail-row-error-${prefix}`}>{visibility.errors[item.key]}</p>}
      {!presenting && <NoteDialog open={open} onOpenChange={setOpen} item={item} product={product} section={section} />}
    </>
  );
};

export default NoteableItem;
